import React, { useMemo, useEffect, useState, useRef, useId } from "react";
import axios from "axios";
import { Settings, Users, ArrowRight } from "lucide-react";
import ShippedByClientDonut from "./ShippedByClientDonut";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { MONTHS_SHORT, parseDateDash } from "./utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Indian fiscal year runs 1 April → 31 March. Returns the April-1 start date
// of the FY containing `ref`. Also returns a short label ("FY26-27") so we
// can surface it in the chart header.
function fyStart(ref = new Date()) {
  const m = ref.getMonth();
  const y = ref.getFullYear();
  const startYear = m >= 3 ? y : y - 1;  // April = 3 (0-indexed)
  const start = new Date(startYear, 3, 1);
  const endYear = startYear + 1;
  const label = `FY${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
  return { start, startYear, endYear, label };
}

// Money formatter — compact, K / M suffixes, no decimals for display.
function fmtMoney(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

// Traffic-light verdict per achievement %: ≥95 green, 70–94 amber, <70 red.
// `text` is the darker shade used for the % label above the bar.
function verdict(pct) {
  if (pct >= 95) return { bar: "#10b981", text: "#059669", key: "green" };
  if (pct >= 70) return { bar: "#f59e0b", text: "#b45309", key: "amber" };
  return { bar: "#ef4444", text: "#dc2626", key: "red" };
}
const NO_PLAN_BAR = "#94a3b8";
const CURRENT_BAR = "#f97316";

const isShipped = (i) =>
  i.stage === "Completed" ||
  (i.stage === "Dispatch" && (i.dispatch_status === "Shipped" || i.dispatch_status === "Delivered"));

// Round the axis up to a friendly step (…, 25, 50, 100, 200, 250, 500 …).
function niceAxis(max) {
  const raw = Math.max(4, max) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((s) => s * pow).find((s) => s >= raw);
  const top = Math.ceil(max / step) * step || step * 4;
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(Math.round(v * 100) / 100);
  return { top, ticks };
}

// Bar with rounded top corners only.
function barPath(x, y, w, h, r) {
  if (h <= 0) return "";
  r = Math.min(r, h, w / 2);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/**
 * DispatchBars — one chart for the whole FY (Tushar, 3-Nov-2026, option A in
 * plans/activity-dispatch-mock.html). Per month:
 *   • the shipped bar (verdict colour when the month had a plan, grey when it
 *     didn't, orange for the current month);
 *   • the plan as a dark tick across the bar — a bar to clear — with the %
 *     achieved above it, or "no plan" for past months without one;
 *   • a slim bar of lines DUE that month and not shipped: hatched amber when the
 *     month is over (that work is late), dashed grey when it's still to come.
 * Hand-drawn SVG rather than recharts so the ticks, labels and hatching sit
 * exactly where the mock put them.
 */
function DispatchBars({ data, curIdx, animate, showPlan }) {
  const wrapRef = useRef(null);
  const [W, setW] = useState(0);
  const [hover, setHover] = useState(null);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const hatchId = `rl-hatch-${uid}`;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    setW(Math.round(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);

  const H = 300, L = 38, R = 8, T = 30, B = 40;
  const pw = Math.max(0, W - L - R), ph = H - T - B;
  const slot = pw / Math.max(1, data.length);
  const maxVal = Math.max(1, ...data.map((d) => Math.max(d.dispatched || 0, d.pipeline || 0, d.target || 0)));
  const { top, ticks } = niceAxis(maxVal * 1.15);
  const y = (v) => T + ph - (v / top) * ph;

  const geo = (i) => {
    const cx = L + slot * i + slot / 2;
    const bw = Math.min(28, slot * 0.36), dw = Math.min(12, slot * 0.16), gap = 3;
    const x0 = cx - (bw + gap + dw) / 2;
    return { cx, bw, dw, gap, x0 };
  };

  const hovered = hover !== null ? data[hover] : null;

  return (
    <div ref={wrapRef} className="relative w-full" style={{ height: H }} data-testid="dispatch-bars">
      {W > 0 && (
        <svg width={W} height={H} className="block overflow-visible" role="img" aria-label="Lines shipped per month against plan">
          <defs>
            <pattern id={hatchId} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="5" height="5" fill="rgba(245,158,11,0.07)" />
              <line x1="0" y1="0" x2="0" y2="5" stroke="rgba(245,158,11,0.45)" strokeWidth="1.6" />
            </pattern>
          </defs>

          {/* Quarters: Q2 and Q4 get a faint band, every quarter a quiet label. */}
          {data.length === 12 && [0, 1, 2, 3].map((q) => (
            <g key={`q${q}`}>
              {q % 2 === 1 && (
                <rect x={L + q * 3 * slot} y={T - 24} width={3 * slot} height={ph + 24} rx="8" fill="hsl(var(--foreground) / 0.025)" />
              )}
              <text x={L + q * 3 * slot + 10} y={T - 10} fontFamily="var(--rl-font-data), sans-serif" fontSize="9.5" fontWeight="700" letterSpacing="1.2" fill="hsl(var(--muted-foreground) / 0.55)">{`Q${q + 1}`}</text>
            </g>
          ))}

          {/* This month: a soft orange column that runs down through its label. */}
          {curIdx >= 0 && (
            <rect x={L + curIdx * slot + 3} y={T - 24} width={Math.max(0, slot - 6)} height={ph + 24 + 34} rx="9" fill="rgba(249,115,22,0.075)" />
          )}
          {hover !== null && (
            <rect x={L + hover * slot + 3} y={T - 24} width={Math.max(0, slot - 6)} height={ph + 24} rx="9" fill="hsl(var(--muted-foreground) / 0.07)" />
          )}

          {ticks.map((v) => (
            <g key={`y${v}`}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={v ? "hsl(var(--border) / 0.55)" : "hsl(var(--border))"} />
              <text x={L - 9} y={y(v) + 3.5} textAnchor="end" fontFamily="var(--rl-font-data), sans-serif" fontSize="10.5" fill="hsl(var(--muted-foreground) / 0.8)" style={{ fontVariantNumeric: "tabular-nums" }}>{v}</text>
            </g>
          ))}

          {data.map((d, i) => {
            const { cx, bw, dw, gap, x0 } = geo(i);
            const isCur = i === curIdx;
            const late = d.isPast;
            const v = d.hasTarget ? verdict(d.pct_achieved) : null;
            const barColour = isCur ? CURRENT_BAR : v ? v.bar : NO_PLAN_BAR;
            const delay = animate ? { animationDelay: `${i * 35}ms` } : undefined;
            const labelY = Math.min(y(d.dispatched || 0), d.hasTarget ? y(d.target) : Infinity) - 8;
            return (
              <g key={d.month}>
                {d.dispatched > 0 && (
                  <path className={animate ? "rl-bar-grow" : undefined} style={delay}
                    d={barPath(x0, y(d.dispatched), bw, y(0) - y(d.dispatched), 4)}
                    fill={barColour} fillOpacity={d.hasTarget || isCur ? 0.9 : 0.75} />
                )}
                {d.pipeline > 0 && (
                  <path className={animate ? "rl-bar-grow" : undefined} style={delay}
                    d={barPath(x0 + bw + gap + 0.5, y(d.pipeline) + 0.5, dw - 1, y(0) - y(d.pipeline) - 0.5, 3)}
                    fill={late ? `url(#${hatchId})` : "hsl(var(--muted-foreground) / 0.05)"}
                    stroke={late ? "rgba(217,119,6,0.6)" : "hsl(var(--muted-foreground) / 0.5)"}
                    strokeDasharray={late ? undefined : "3 2"} />
                )}
                {d.hasTarget && (
                  <g className={animate ? "rl-bar-late" : undefined}>
                    <line x1={x0 - 5} x2={x0 + bw + 5} y1={y(d.target)} y2={y(d.target)} stroke="hsl(var(--card))" strokeWidth="5" strokeLinecap="round" />
                    <line x1={x0 - 5} x2={x0 + bw + 5} y1={y(d.target)} y2={y(d.target)} stroke="hsl(var(--foreground) / 0.85)" strokeWidth="2.2" strokeLinecap="round" />
                  </g>
                )}
                {showPlan && ((d.isPast && (d.hasTarget || d.dispatched > 0)) || (isCur && d.hasTarget)) && (
                  <text className={animate ? "rl-bar-late" : undefined} x={x0 + bw / 2} y={labelY} textAnchor="middle"
                    fontFamily={d.hasTarget ? "var(--rl-font-data), sans-serif" : "var(--rl-font-body), sans-serif"}
                    fontSize={d.hasTarget ? 10.5 : 10} fontWeight={d.hasTarget ? 700 : 400}
                    fill={!d.hasTarget ? "hsl(var(--muted-foreground) / 0.7)" : isCur ? "hsl(var(--muted-foreground))" : v.text}>
                    {d.hasTarget ? `${d.pct_achieved}%` : "no plan"}
                  </text>
                )}
                <text x={cx} y={H - B + 18} textAnchor="middle" fontFamily="var(--rl-font-body), sans-serif" fontSize="12"
                  fontWeight={isCur ? 700 : 400}
                  fill={isCur ? "hsl(var(--foreground))" : d.isFuture ? "hsl(var(--muted-foreground) / 0.6)" : "hsl(var(--muted-foreground))"}>
                  {d.short}
                </text>
                {isCur ? (
                  <text x={cx} y={H - B + 31} textAnchor="middle" fontFamily="var(--rl-font-body), sans-serif" fontSize="9.5" fontWeight="600" fill="#ea580c">now</text>
                ) : (i === 0 || d.monthIdx === 0) ? (
                  <text x={cx} y={H - B + 31} textAnchor="middle" fontFamily="var(--rl-font-data), sans-serif" fontSize="9.5" fill="hsl(var(--muted-foreground) / 0.55)">{d.year}</text>
                ) : null}
                <rect x={L + slot * i} y={T - 24} width={slot} height={ph + 24} fill="transparent"
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} data-testid={`dispatch-month-${i}`} />
              </g>
            );
          })}
        </svg>
      )}

      {hovered && (
        <div
          className="absolute z-10 pointer-events-none min-w-[188px] rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.25)] text-[11.5px]"
          style={{ top: 34, left: Math.max(0, Math.min(W - 196, L + slot * (hover + 1) + 4 > W - 196 ? L + slot * hover - 196 : L + slot * (hover + 1) + 4)) }}
        >
          <div className="font-heading font-semibold text-[12px] text-[hsl(var(--foreground))] mb-1">
            {MONTHS_SHORT[hovered.monthIdx]} {hovered.year}{hovered.isCurrent && <span className="ml-1.5 font-sans font-medium text-[10.5px] text-orange-600 dark:text-orange-400">this month</span>}
          </div>
          {[
            !hovered.isFuture && ["Shipped", `${hovered.dispatched} line${hovered.dispatched === 1 ? "" : "s"}${hovered.dispatched_value_usd ? ` · ${fmtMoney(hovered.dispatched_value_usd)}` : ""}`],
            ["Plan", hovered.hasTarget ? `${hovered.target} lines${hovered.target_value_usd ? ` · ${fmtMoney(hovered.target_value_usd)}` : ""}` : "not set"],
            hovered.hasTarget && !hovered.isFuture && ["Achieved", `${hovered.pct_achieved}%`, hovered.isCurrent ? null : verdict(hovered.pct_achieved).text],
            [hovered.isPast ? "Due, still not shipped" : "Due, not shipped yet", `${hovered.pipeline} line${hovered.pipeline === 1 ? "" : "s"}`, hovered.isPast && hovered.pipeline ? "#b45309" : null],
          ].filter(Boolean).map(([k, v, c]) => (
            <div key={k} className="flex justify-between gap-4 leading-[1.6] text-[hsl(var(--muted-foreground))]">
              <span>{k}</span>
              <b className="font-mono-data font-bold text-[hsl(var(--foreground))]" style={c ? { color: c } : undefined}>{v}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Tiny running total beside the "Against plan" number: shipped (green) vs
// planned (dashed orange), April to now.
function PlanSpark({ actual, planned }) {
  const w = 92, h = 30;
  const n = Math.max(actual.length, planned.length);
  const max = Math.max(1, ...actual, ...planned);
  const x = (i) => 2 + (i * (w - 4)) / Math.max(1, n - 1);
  const y = (v) => h - 2 - (v / max) * (h - 4);
  const path = (arr) => arr.map((v, i) => `${i ? "L" : "M"}${x(i)},${y(v)}`).join("");
  return (
    <svg width={w} height={h} className="ml-2.5 mb-px overflow-visible shrink-0" aria-hidden="true">
      {planned.length > 1 && <path d={path(planned)} fill="none" stroke="#f97316" strokeWidth="1.6" strokeDasharray="3 2.5" strokeLinecap="round" />}
      {actual.length > 1 && <path d={path(actual)} fill="none" stroke="#10b981" strokeWidth="1.8" strokeLinejoin="round" />}
      {actual.length > 0 && <circle cx={x(actual.length - 1)} cy={y(actual[actual.length - 1])} r="2.4" fill="#10b981" />}
    </svg>
  );
}

// One KPI stack, built like the hero's: label / 28px number / 11px caption,
// with an optional line hung below.
function Kpi({ label, children, caption, hang, testId }) {
  return (
    <div className="relative flex flex-col gap-0.5 min-w-0" data-testid={testId}>
      <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70 whitespace-nowrap">{label}</span>
      <span className="flex items-end mt-1">{children}</span>
      <span className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-tight whitespace-nowrap">{caption}</span>
      {hang && <span className="absolute left-0 top-full mt-1.5 flex items-center gap-2 font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))]/75 whitespace-nowrap">{hang}</span>}
    </div>
  );
}
const BIG = "font-mono-data text-[1.5rem] md:text-[1.75rem] leading-none font-bold tracking-tight tabular-nums";

/**
 * MonthlyDispatchesChart — the FY's dispatches against plan.
 *
 * Redesigned 3-Nov-2026 (option A in plans/activity-dispatch-mock.html): a
 * hero-style KPI strip (this month · FY so far · against plan) over ONE chart.
 * The old cumulative chart became the "Against plan" number + its sparkline.
 * "Against plan" only counts finished months that HAD a plan — the old strip
 * counted October's shipments against no plan and overstated the lead.
 */
export default function MonthlyDispatchesChart({
  items, scrapSales = {}, targetVersion = 0,
  // false = draw bars straight away (dashboard revisits — see lib/dashboardIntro).
  animate = true,
  // Month's-target props (optional — omit to hide the KPI strip)
  target, isAdmin, canSetTarget, onSetTarget, onManageSetters, onDispatchedClick,
}) {
  const [targetsByKey, setTargetsByKey] = useState({}); // "YYYY-MM" → target summary
  const [refreshing, setRefreshing] = useState(false);
  const [clientDonutOpen, setClientDonutOpen] = useState(false); // chairman's shipped-by-client view
  const now = new Date();

  // Fetch every month of the current FY (Apr → Mar, 12 months) in one call.
  // Re-runs whenever `targetVersion` is bumped by the parent (i.e. a target
  // was just saved) so the chart reflects the new plan immediately.
  useEffect(() => {
    const { start } = fyStart(new Date());
    const end = new Date(start.getFullYear() + 1, 2, 1); // March of next year
    if (targetVersion > 0) setRefreshing(true);
    axios.get(`${API}/monthly-targets`, {
      params: {
        from_year: start.getFullYear(), from_month: start.getMonth() + 1,
        to_year: end.getFullYear(), to_month: end.getMonth() + 1,
      },
    }).then(r => {
      const map = {};
      (r.data || []).forEach(t => {
        map[`${t.year}-${String(t.month).padStart(2, "0")}`] = t;
      });
      setTargetsByKey(map);
    }).catch(() => { /* chart degrades gracefully */ })
      .finally(() => {
        // Hold the sweep animation briefly so it's perceptible even on fast
        // networks. The CSS animation itself runs 700ms — match.
        setTimeout(() => setRefreshing(false), 700);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetVersion]);

  const data = useMemo(() => {
    const { start } = fyStart(now);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const rows = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const year = d.getFullYear();
      const month = d.getMonth();
      const isCurrent = d.getTime() === thisMonth.getTime();
      const isFuture = d > thisMonth;
      const isPast = d < thisMonth;
      // Dispatched: bucket by actual ship date (shipped_at) — matches header strip and backend counter
      const dispatchedItems = items.filter(it => {
        if (!it.shipped_at || !isShipped(it)) return false;
        const sd = new Date(it.shipped_at);
        return !isNaN(sd) && sd.getFullYear() === year && sd.getMonth() === month;
      });
      // Due that month and not shipped yet. For past months that is LATE work.
      const pipelineItems = items.filter(it => {
        const dd = parseDateDash(it.dispatch_due_date || it.due_date);
        return dd && !isNaN(dd) && dd.getFullYear() === year && dd.getMonth() === month && !isShipped(it);
      });
      const key = `${year}-${String(month + 1).padStart(2, "0")}`;
      const t = targetsByKey[key] || {};
      const targetN = t.total || 0;
      const hasTarget = targetN > 0;
      rows.push({
        month: `${MONTHS_SHORT[month]} '${String(year).slice(-2)}`,
        short: MONTHS_SHORT[month],
        monthIdx: month,
        year,
        dispatched: dispatchedItems.length,
        dispatched_value_usd: Math.round(dispatchedItems.reduce((s, it) => s + (Number(it.value_usd) || 0), 0)),
        pipeline: pipelineItems.length,
        target: hasTarget ? targetN : null,
        target_value_usd: t.target_value_usd || 0,
        pct_achieved: hasTarget ? Math.round((dispatchedItems.length / targetN) * 100) : 0,
        hasTarget,
        isFuture, isCurrent, isPast,
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, targetsByKey]);

  const curIdx = data.findIndex(d => d.isCurrent);
  const current = data[curIdx] || {};
  const showPlan = data.some(d => d.hasTarget);

  // "Total shipped this month" — ALL items with an explicit shipped_at in the
  // current calendar month. The canonical count for the badge and the lightbox.
  const totalShippedThisMonth = current.dispatched || 0;

  // PO Line Wise KPI — items from the curated target list shipped this month.
  // Intentionally a subset of totalShippedThisMonth.
  const poLinesKpiDispatched = useMemo(() => {
    if (!target?.item_ids?.length) return 0;
    const cy = now.getFullYear();
    const cm = now.getMonth();
    const idSet = new Set(target.item_ids);
    return items.filter(item => {
      if (!idSet.has(item.id) || !item.shipped_at || !isShipped(item)) return false;
      const d = new Date(item.shipped_at);
      return d.getFullYear() === cy && d.getMonth() === cm;
    }).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, target?.item_ids]);

  const isRaceMode = target?.target_type === "race";
  const hasMonthTarget = !!target?.hasTarget;
  // KPI dispatched count: race → all shipped this month; po_lines → target-list shipped this month
  const kpiDispatched = isRaceMode ? totalShippedThisMonth : poLinesKpiDispatched;
  const kpiTotal = target?.total ?? 0;
  const kpiPct = kpiTotal > 0 ? Math.min(100, Math.round((kpiDispatched / kpiTotal) * 100)) : 0;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const monthName = target?.monthName || now.toLocaleString("en-GB", { month: "long" });

  // FY so far + against plan. Plan only counts FINISHED months that had one.
  const fy = useMemo(() => {
    const shipped = data.filter(d => !d.isFuture).reduce((s, d) => s + d.dispatched, 0);
    const planned = data.filter(d => d.isPast && d.hasTarget);
    const shipPlanned = planned.reduce((s, d) => s + d.dispatched, 0);
    const planSum = planned.reduce((s, d) => s + d.target, 0);
    const range = planned.length
      ? (planned.length === 1 ? planned[0].short : `${planned[0].short}–${planned[planned.length - 1].short}`)
      : "";
    // Running totals for the sparkline, April → this month.
    const actualRun = [], planRun = [];
    let a = 0, p = 0;
    data.forEach(d => {
      if (d.isFuture) return;
      a += d.dispatched; actualRun.push(a);
      if (d.hasTarget) { p += d.target; planRun.push(p); }
    });
    return { shipped, hasPlan: planned.length > 0, shipPlanned, planSum, delta: shipPlanned - planSum, range, actualRun, planRun };
  }, [data]);

  const prev = curIdx > 0 ? data[curIdx - 1] : null;

  // Legend lists only what's on the chart.
  const legend = useMemo(() => {
    const past = data.filter(d => d.isPast && d.dispatched > 0);
    const keys = new Set(past.filter(d => d.hasTarget).map(d => verdict(d.pct_achieved).key));
    return {
      green: keys.has("green"), amber: keys.has("amber"), red: keys.has("red"),
      noPlan: past.some(d => !d.hasTarget),
      late: data.some(d => d.isPast && d.pipeline > 0),
      later: data.some(d => !d.isPast && d.pipeline > 0),
    };
  }, [data]);

  const canTarget = isAdmin || canSetTarget;

  return (
    <div className={`relative rl-widget-glass rounded-xl pl-7 pr-6 pt-[18px] pb-[18px] overflow-hidden h-full flex flex-col ${refreshing ? "rl-chart-refresh" : ""}`} data-testid="monthly-dispatches-chart">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
          Monthly Dispatches
          <span className="normal-case tracking-[0.02em] font-mono-data font-semibold text-[10px] text-orange-700 dark:text-orange-400 bg-orange-500/[0.08] rounded-full px-[7px] py-[2px]" data-testid="chart-fy-label">{fyStart(now).label}</span>
        </h3>
        <div className="flex items-center gap-1.5">
          <TooltipProvider delayDuration={200}>
            {isAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" onClick={onManageSetters}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-well text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] transition-colors"
                    data-testid="manage-target-setters-btn" aria-label="Manage target setters">
                    <Users className="w-[15px] h-[15px]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4} className="text-[11px]">Manage target setters</TooltipContent>
              </Tooltip>
            )}
            {canTarget && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" onClick={onSetTarget}
                    className="group/tgt inline-flex items-center justify-center w-7 h-7 rounded-full rl-well text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] transition-colors"
                    data-testid="set-monthly-target-btn" aria-label={hasMonthTarget ? "Edit target" : "Set target"}>
                    <Settings className="w-[15px] h-[15px] transition-transform group-hover/tgt:rotate-45" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4} className="text-[11px]">{hasMonthTarget ? `Edit ${monthName}'s target` : `Set ${monthName}'s target`}</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>
      </div>

      {/* ── KPI strip: built like the hero's stacks ── */}
      {target && (
        <div className="flex items-stretch gap-6 flex-wrap pb-[26px] mb-1.5 border-b border-[hsl(var(--border))]/60" data-testid="chart-kpis">
          <Kpi
            label={monthName}
            testId="kpi-this-month"
            caption={`${hasMonthTarget && !isRaceMode ? "target lines shipped" : "shipped"} · day ${now.getDate()} of ${daysInMonth}`}
            hang={hasMonthTarget ? (
              <>
                <span className="w-[120px] h-[5px] rounded-full bg-[hsl(var(--muted))] overflow-hidden shrink-0">
                  <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${kpiPct}%` }} />
                </span>
                <span>{Math.max(0, kpiTotal - kpiDispatched)} to go{!isRaceMode ? ` · ${totalShippedThisMonth} shipped in all` : ""}</span>
              </>
            ) : (
              <span>
                {current.pipeline || 0} due this month
                {canTarget && (
                  <> · <button type="button" onClick={onSetTarget} className="font-sans font-semibold text-orange-700 dark:text-orange-400 hover:underline" data-testid="kpi-set-target">Set target</button></>
                )}
              </span>
            )}
          >
            <button type="button" onClick={() => onDispatchedClick?.()} className={`${BIG} text-[hsl(var(--foreground))] hover:text-orange-600 transition-colors`} title="See this month's shipments" data-testid="open-dispatch-lightbox">
              {hasMonthTarget ? kpiDispatched : totalShippedThisMonth}
            </button>
            {hasMonthTarget && <span className="font-mono-data text-[15px] font-semibold text-[hsl(var(--muted-foreground))] ml-1.5 mb-px">/ {kpiTotal}</span>}
          </Kpi>

          <div className="w-px self-stretch bg-[hsl(var(--border))]/70" aria-hidden="true" />
          <Kpi label="FY so far" caption="lines shipped since 1 Apr" testId="kpi-fy">
            <span className={`${BIG} text-[hsl(var(--foreground))]`}>{fy.shipped}</span>
          </Kpi>

          {fy.hasPlan ? (
            <>
              <div className="w-px self-stretch bg-[hsl(var(--border))]/70" aria-hidden="true" />
              <Kpi
                label="Against plan"
                testId="kpi-against-plan"
                caption={`${fy.delta >= 0 ? "ahead" : "behind"} · ${fy.shipPlanned} shipped vs ${fy.planSum} planned, ${fy.range}`}
              >
                <span className={BIG} style={{ color: fy.delta >= 0 ? "#059669" : "#d97706" }}>{fy.delta >= 0 ? "+" : "−"}{Math.abs(fy.delta)}</span>
                <PlanSpark actual={fy.actualRun} planned={fy.planRun} />
              </Kpi>
            </>
          ) : prev ? (
            <>
              <div className="w-px self-stretch bg-[hsl(var(--border))]/70" aria-hidden="true" />
              <Kpi label={`${MONTHS_SHORT[prev.monthIdx]} ${prev.year}`} caption="lines shipped last month" testId="kpi-last-month">
                <span className={`${BIG} text-[hsl(var(--foreground))]`}>{prev.dispatched}</span>
              </Kpi>
            </>
          ) : null}

          {hasMonthTarget && (
            <>
              <div className="w-px self-stretch bg-[hsl(var(--border))]/70" aria-hidden="true" />
              <Kpi label="Value shipped" caption={current.target_value_usd ? `of ${fmtMoney(current.target_value_usd)} plan` : "this month"} testId="kpi-value">
                <span className={`${BIG} text-[hsl(var(--foreground))]`}>{fmtMoney(current.dispatched_value_usd)}</span>
              </Kpi>
            </>
          )}

          <div className="ml-auto flex flex-col justify-center items-end gap-1">
            <button type="button" onClick={() => onDispatchedClick?.()} className="rl-chip-host focus-visible:outline-none" data-testid="open-dispatch-lightbox-total">
              <span className="rl-open-chip">This month's list <ArrowRight className="rl-open-arrow w-3 h-3" strokeWidth={2.4} /></span>
            </button>
            <button type="button" onClick={() => setClientDonutOpen(true)} className="rl-chip-host focus-visible:outline-none" data-testid="open-client-donut">
              <span className="rl-open-chip">By client <ArrowRight className="rl-open-arrow w-3 h-3" strokeWidth={2.4} /></span>
            </button>
          </div>
        </div>
      )}

      <DispatchBars data={data} curIdx={curIdx} animate={animate} showPlan={showPlan} />

      {/* Legend — plain words, only what's on the chart */}
      <div className="flex items-center gap-x-[18px] gap-y-1.5 mt-1.5 text-[11px] text-[hsl(var(--muted-foreground))] flex-wrap" data-testid="chart-legend">
        {legend.green && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] bg-emerald-500" />Shipped, met plan</span>}
        {legend.amber && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] bg-amber-500" />Shipped, 70–94% of plan</span>}
        {legend.red && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] bg-red-500" />Shipped, under 70% of plan</span>}
        {legend.noPlan && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] opacity-75" style={{ background: NO_PLAN_BAR }} />Shipped, no plan set</span>}
        {showPlan && (
          <span className="inline-flex items-center gap-1.5">
            <svg width="18" height="10" aria-hidden="true"><line x1="2" x2="16" y1="5" y2="5" stroke="hsl(var(--foreground) / 0.85)" strokeWidth="2.2" strokeLinecap="round" /></svg>Plan
          </span>
        )}
        {legend.late && (
          <span className="inline-flex items-center gap-1.5">
            <svg width="10" height="12" aria-hidden="true">
              <defs><pattern id="rl-hatch-legend" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="4" stroke="rgba(245,158,11,0.5)" strokeWidth="1.4" /></pattern></defs>
              <rect x=".5" y=".5" width="9" height="11" rx="2" fill="url(#rl-hatch-legend)" stroke="rgba(217,119,6,0.6)" />
            </svg>
            Was due that month, still not shipped (late)
          </span>
        )}
        {legend.later && (
          <span className="inline-flex items-center gap-1.5">
            <svg width="10" height="12" aria-hidden="true"><rect x=".5" y=".5" width="9" height="11" rx="2" fill="hsl(var(--muted-foreground) / 0.05)" stroke="hsl(var(--muted-foreground) / 0.5)" strokeDasharray="3 2" /></svg>
            Due later
          </span>
        )}
      </div>

      {/* Chairman's view: this month's shipments sliced by client / group */}
      <ShippedByClientDonut
        open={clientDonutOpen}
        onClose={() => setClientDonutOpen(false)}
        items={items}
        monthLabel={current.month || `${MONTHS_SHORT[now.getMonth()]} '${String(now.getFullYear()).slice(-2)}`}
        scrapThisMonth={scrapSales?.can_view ? (scrapSales?.value_usd_this_month || 0) : 0}
        canViewScrap={!!scrapSales?.can_view}
      />
    </div>
  );
}
// end of file
